import { Component, ReactNode } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import { Button } from './catalyst/button'
import { logger } from '@/lib/logger'
import { captureException, setContext } from '@/lib/error-tracking'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  onReset?: () => void
  level?: 'root' | 'route' | 'component'
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

/**
 * Error Boundary component for graceful error handling
 * Prevents entire app crashes and provides recovery options
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error with context
    logger.error('ErrorBoundary caught error', error, {
      level: this.props.level || 'component',
      componentStack: errorInfo.componentStack,
    })

    // Send to error tracking service with React-specific context
    setContext('react', {
      componentStack: errorInfo.componentStack,
      boundaryLevel: this.props.level || 'component',
    })
    captureException(error)

    this.setState({
      error,
      errorInfo,
    })
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })

    if (this.props.onReset) {
      this.props.onReset()
    }
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default error UI based on boundary level
      const { level = 'component' } = this.props
      const { error } = this.state

      // Root-level error (most severe)
      if (level === 'root') {
        return (
          <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 dark:bg-slate-950">
            <div className="w-full max-w-md text-center">
              <div className="mb-6 flex justify-center">
                <div className="rounded-full bg-red-100 p-4 dark:bg-red-950/30">
                  <AlertTriangle className="h-12 w-12 text-red-600 dark:text-red-400" />
                </div>
              </div>

              <h1 className="mb-2 text-2xl font-bold text-slate-900 dark:text-white">
                Something went wrong
              </h1>

              <p className="mb-6 text-slate-600 dark:text-slate-400">
                We're sorry, but something unexpected happened. Please try refreshing the page.
              </p>

              {import.meta.env.DEV && error && (
                <div className="mb-6 rounded-lg bg-slate-100 p-4 text-left dark:bg-slate-900">
                  <p className="mb-2 text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Error Details (Dev Only):
                  </p>
                  <p className="text-xs text-red-600 dark:text-red-400">
                    {error.message}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={this.handleReset}
                  className="flex-1"
                  color="purple"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </Button>
                <Button
                  onClick={this.handleGoHome}
                  className="flex-1"
                  plain
                >
                  <Home className="h-4 w-4" />
                  Go Home
                </Button>
              </div>
            </div>
          </div>
        )
      }

      // Route-level error
      if (level === 'route') {
        return (
          <div className="flex min-h-[400px] items-center justify-center px-4">
            <div className="w-full max-w-md text-center">
              <div className="mb-4 flex justify-center">
                <div className="rounded-full bg-red-100 p-3 dark:bg-red-950/30">
                  <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
                </div>
              </div>

              <h2 className="mb-2 text-xl font-bold text-slate-900 dark:text-white">
                Failed to load page
              </h2>

              <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
                An error occurred while loading this page. You can try again or go back to the dashboard.
              </p>

              {import.meta.env.DEV && error && (
                <div className="mb-4 rounded-lg bg-slate-100 p-3 text-left dark:bg-slate-900">
                  <p className="mb-1 text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Error: {error.message}
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={this.handleReset}
                  className="flex-1"
                  color="purple"
                >
                  <RefreshCw className="h-4 w-4" />
                  Retry
                </Button>
                <Button
                  onClick={this.handleGoHome}
                  className="flex-1"
                  plain
                >
                  Dashboard
                </Button>
              </div>
            </div>
          </div>
        )
      }

      // Component-level error (least severe)
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/20">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-900 dark:text-red-300">
                Component Error
              </h3>
              <p className="mt-1 text-sm text-red-800 dark:text-red-400">
                {error?.message || 'Failed to render this component'}
              </p>
              <button
                onClick={this.handleReset}
                className="mt-2 text-sm font-medium text-red-700 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
